import React, { useEffect, useRef, useState } from 'react';
import {
  addDays, addMonths, endOfMonth, endOfWeek, format, isBefore, isSameDay, isSameMonth, parseISO, startOfMonth, startOfWeek,
} from 'date-fns';
import { AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const toISO = (d) => format(d, 'yyyy-MM-dd');
const KEY_STEPS = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };

const monthDays = (month) => {
  const days = [];
  for (let d = startOfWeek(startOfMonth(month)); d <= endOfWeek(endOfMonth(month)); d = addDays(d, 1)) days.push(d);
  return days;
};

const NavButton = ({ label, disabled, onClick, testId, children }) => (
  <button type="button" onClick={onClick} disabled={disabled} aria-label={label} data-testid={testId}
    className="h-11 w-11 inline-flex items-center justify-center rounded-full border border-[#C6A14A]/30 text-[#C6A14A] transition-colors hover:bg-[#C6A14A]/10 disabled:opacity-25 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C6A14A]/60">
    {children}
  </button>
);

const dayClass = (selected, isToday, disabled) => [
  'h-11 w-full rounded-full text-[15px] transition-[background-color,color,box-shadow] duration-200',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C6A14A] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a1120]',
  selected ? 'bg-[#C6A14A] text-[#050816] font-semibold'
    : disabled ? 'text-white/20 cursor-not-allowed'
      : `text-[#E8E4DE] hover:bg-[#C6A14A]/15 ${isToday ? 'ring-1 ring-[#C6A14A]/70' : ''}`,
].join(' ');

// Inline navy & gold calendar; value/min are YYYY-MM-DD strings.
export const LuxuryCalendar = ({ id, label, required, value, min, onChange, testId, error }) => {
  const minDate = parseISO(min);
  const selected = value ? parseISO(value) : null;
  const [month, setMonth] = useState(() => startOfMonth(selected && !isBefore(selected, minDate) ? selected : minDate));
  const [focusDate, setFocusDate] = useState(() => selected || minDate);
  const [slide, setSlide] = useState(1);
  const gridRef = useRef(null);
  const keyNav = useRef(false);

  useEffect(() => {
    if (!keyNav.current) return;
    keyNav.current = false;
    gridRef.current?.querySelector(`[data-date="${toISO(focusDate)}"]`)?.focus();
  }, [focusDate, month]);

  const changeMonth = (delta) => { setSlide(delta); setMonth((m) => addMonths(m, delta)); };
  const canGoBack = isBefore(startOfMonth(minDate), month);
  const today = new Date();

  const moveFocus = (next) => {
    const target = isBefore(next, minDate) ? minDate : next;
    keyNav.current = true;
    if (!isSameMonth(target, month)) { setSlide(isBefore(target, month) ? -1 : 1); setMonth(startOfMonth(target)); }
    setFocusDate(target);
  };

  const onKeyDown = (e, day) => {
    if (KEY_STEPS[e.key]) moveFocus(addDays(day, KEY_STEPS[e.key]));
    else if (e.key === 'Home') moveFocus(startOfWeek(day));
    else if (e.key === 'End') moveFocus(endOfWeek(day));
    else if (e.key === 'PageUp') moveFocus(addMonths(day, -1));
    else if (e.key === 'PageDown') moveFocus(addMonths(day, 1));
    else return;
    e.preventDefault();
  };

  const tabDate = isSameMonth(focusDate, month) ? focusDate : (selected && isSameMonth(selected, month) ? selected : (isSameMonth(minDate, month) ? minDate : startOfMonth(month)));
  const headingId = `${id}-month`;

  return (
    <div className="space-y-3" data-testid={testId}>
      <p id={`${id}-label`} className="text-[#E8E4DE]/80 text-sm">{label}{required && <span className="text-[#C6A14A]"> *</span>}</p>
      <div className="rounded-2xl border border-[#C6A14A]/40 bg-[#0a1120] p-2 sm:p-4 shadow-[0_20px_60px_-30px_rgba(198,161,74,0.35)] overflow-hidden">
        <div className="flex items-center justify-between px-1 pb-3">
          <NavButton label="Previous month" disabled={!canGoBack} onClick={() => changeMonth(-1)} testId={`${testId}-prev`}><ChevronLeft className="h-5 w-5" strokeWidth={1.5} /></NavButton>
          <h3 id={headingId} aria-live="polite" className="font-serif text-xl sm:text-2xl text-white tracking-wide" data-testid={`${testId}-month`}>{format(month, 'MMMM yyyy')}</h3>
          <NavButton label="Next month" onClick={() => changeMonth(1)} testId={`${testId}-next`}><ChevronRight className="h-5 w-5" strokeWidth={1.5} /></NavButton>
        </div>
        <div className="grid grid-cols-7 gap-0.5 sm:gap-1 pb-1" aria-hidden="true">
          {WEEKDAYS.map((w) => <span key={w} className="text-center text-[11px] uppercase tracking-[0.15em] text-[#C6A14A]/70 py-1">{w.slice(0, 2)}</span>)}
        </div>
        <div key={toISO(month)} ref={gridRef} role="group" aria-labelledby={`${id}-label ${headingId}`}
          className={`grid grid-cols-7 gap-0.5 sm:gap-1 animate-in fade-in duration-300 ${slide < 0 ? 'slide-in-from-left-4' : 'slide-in-from-right-4'}`}>
          {monthDays(month).map((day) => {
            if (!isSameMonth(day, month)) return <span key={toISO(day)} aria-hidden="true" />;
            const iso = toISO(day);
            const disabled = isBefore(day, minDate);
            const isSel = !!selected && isSameDay(day, selected);
            return (
              <button key={iso} type="button" data-date={iso} data-testid={`${testId}-day-${iso}`}
                aria-label={format(day, 'EEEE, MMMM d, yyyy')} aria-pressed={isSel} disabled={disabled}
                aria-current={isSameDay(day, today) ? 'date' : undefined}
                tabIndex={isSameDay(day, tabDate) ? 0 : -1}
                onClick={() => { if (!disabled) { setFocusDate(day); onChange(iso); } }}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ' ') && !disabled) { e.preventDefault(); onChange(iso); return; }
                  onKeyDown(e, day);
                }}
                className={dayClass(isSel, isSameDay(day, today), disabled)}>
                {format(day, 'd')}
              </button>
            );
          })}
        </div>
      </div>
      <p className="text-center font-serif text-lg text-[#C6A14A] min-h-[28px]" aria-live="polite" data-testid={`${testId}-chosen`}>
        {selected ? format(selected, 'EEEE, MMMM d, yyyy') : <span className="font-sans text-sm text-[#E8E4DE]/40">No date picked yet</span>}
      </p>
      {error && (
        <div role="alert" className="flex items-center gap-2 p-3 text-sm text-red-400 bg-red-400/10 border border-red-400/20 rounded-lg" data-testid="signup-error">
          <AlertCircle className="h-4 w-4 shrink-0" /><span>{error}</span>
        </div>
      )}
    </div>
  );
};
